import clsx from 'clsx'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { CitationSource } from 'types/chat-message'
import { getFileExtension, getFileIcon } from 'utils/chat-attachments'
import { getSourceTitle, getStoredFileName } from 'utils/citations'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'
import { useOpenSource } from 'components/notebook/DocumentPreviewContext'
import { DownloadFileButton } from 'components/messages/DownloadFileButton'

type ToolFileCardProps = {
  source: CitationSource
}

// The color users associate with each format (Acrobat red, Word blue, Excel green, ...); others keep the brand color.
const FILE_COLORS: Partial<Record<IconName, string>> = {
  'file-pdf': 'bg-red-500/10 text-red-600 dark:text-red-400',
  'file-word': 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  'file-excel': 'bg-green-500/10 text-green-600 dark:text-green-400',
  'file-powerpoint': 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  'file-image': 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  'file-archive': 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  'file-lines': 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  'file-code': 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
}

/**
 * A document an agent tool produced (e.g. generate_pdf): the card opens it like any source (notebook →
 * side panel), the button on the right saves it. Two sibling controls: a button can't hold another.
 */
export const ToolFileCard = ({ source }: ToolFileCardProps) => {
  const openSource = useOpenSource()
  const url = source.url ?? ''
  const title = getSourceTitle(source)
  const extension = getFileExtension(url)
  const icon = getFileIcon(url)

  return (
    <div className="flex w-full max-w-md items-center rounded-xl border border-border bg-background pr-2 transition-colors hover:border-primary/40 hover:bg-muted">
      <button
        type="button"
        title={title}
        onClick={() => {
          analytics.track(ANALYTICS_EVENTS.SOURCE_CLICKED, { source_type: source.type })
          void openSource(source)
        }}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl p-2.5 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid"
      >
        <span
          className={clsx(
            'flex size-10 shrink-0 items-center justify-center rounded-lg',
            FILE_COLORS[icon] ?? 'bg-primary/10 text-primary',
          )}
        >
          <OdinIcon icon={['far', icon]} className="size-5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-medium text-foreground">{title}</span>
          {extension && <span className="text-xs text-muted-foreground uppercase">{extension}</span>}
        </span>
      </button>
      <DownloadFileButton
        url={url}
        fileName={getStoredFileName(url) || title}
        className="shrink-0 text-muted-foreground hover:text-primary"
      />
    </div>
  )
}
